import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Logger from "../../../services/Logger";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    Grid,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomSnackbar from "../../components/CustomSnackbar";
import GenreService from "../../setting/genre/GenreServie";
import { getContentLanguage } from "../../../services/LangPrefs";
import AdminCrossService from "./AdminCrossService";

export default function CrossCreateTemplate() {
    const navigate = useNavigate();
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState("success");
    const [message, setMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [genres, setGenres] = useState([]);
    const [selectedGenreId, setSelectedGenreId] = useState("");
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        const fetchGenres = async () => {
            try {
                const result = await GenreService.requestRootGenre();
                if (result && result.length > 0) {
                    setGenres(result);
                    // Set News as default (first item with name "News")
                    const newsGenre = result.find(genre => genre.name === "News");
                    if (newsGenre) {
                        setSelectedGenreId(newsGenre.id);
                    } else {
                        setSelectedGenreId(result[0].id);
                    }
                }
            } catch (error) {
                Logger.error("Error fetching genres:", error);
            }
        };
        fetchGenres();
    }, []);

    const handleCreate = async () => {
        if (!title.trim() || !content.trim()) {
            handleSnackBar("error", "Please fill in both title and content");
            return;
        }

        if (!selectedGenreId) {
            handleSnackBar("error", "Please select a genre");
            return;
        }

        setIsCreating(true);
        setErrorMessage(""); // Clear any previous error messages

        try {
            const language = getContentLanguage();
            const result = await AdminCrossService.createTemplate({
                title: title.trim(),
                content: content.trim(),
                genreId: selectedGenreId,
                language: language,
            });

            // Check if the response indicates failure
            if (result && result.code === "FAILURE") {
                setErrorMessage(result.message || "Failed to create template");
                return;
            }

            handleSnackBar("success", "Template created successfully");

            // Navigate back to the template list after a short delay
            setTimeout(() => {
                navigate("/admin/puzzle/cross_tpl");
            }, 1500);
        } catch (error) {
            Logger.error("Error creating template:", error);
            setErrorMessage(error.message || "Error creating template");
        } finally {
            setIsCreating(false);
        }
    };

    const handleSnackBar = (severity, message) => {
        setOpenSnackbar(true);
        setSeverity(severity);
        setMessage(message);
    };

    const handleBack = () => {
        navigate("/admin/puzzle/cross_tpl");
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            sx={{ mr: 2 }}
                        >
                            Back
                        </Button>
                        <Box>
                            <Typography variant="h4" component="h1">
                                Create Crossword Template
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                This template is for Crossword puzzles
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    {errorMessage && (
                        <Alert 
                            severity="warning" 
                            onClose={() => setErrorMessage("")}
                            sx={{ mb: 2 }}
                        >
                            {errorMessage}
                        </Alert>
                    )}
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            {/* Select a genre for the template */}
                            <Box sx={{
                                position: 'relative',
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                p: 2,
                                pt: 3
                            }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        position: 'absolute',
                                        top: '-8px',
                                        left: '8px',
                                        backgroundColor: 'white',
                                        px: 1,
                                        color: 'text.secondary',
                                        fontSize: '0.75rem'
                                    }}
                                >
                                    Genre
                                </Typography>
                                <FormGroup row>
                                    {genres.map((genre) => (
                                        <FormControlLabel
                                            key={genre.id}
                                            control={
                                                <Checkbox
                                                    checked={selectedGenreId === genre.id}
                                                    onChange={() => setSelectedGenreId(genre.id)}
                                                    disabled={!genre.active}
                                                />
                                            }
                                            label={`${genre.name}${!genre.active ? ' (Inactive)' : ''}`}
                                            sx={{
                                                opacity: genre.active ? 1 : 0.6,
                                                mr: 2,
                                            }}
                                        />
                                    ))}
                                </FormGroup>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Enter template title"
                                variant="standard"
                                required
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Content"
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                placeholder="Enter template content"
                                variant="standard"
                                multiline
                                rows={8}
                                required
                                helperText="Enter a substantial text passage. We'll extract words from it to create the puzzle. Short content won't provide enough words."
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    variant="contained"
                                    onClick={handleCreate}
                                    disabled={!title.trim() || !content.trim() || !selectedGenreId || isCreating}
                                    size="large"
                                    startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : null}
                                >
                                    {isCreating ? "Creating..." : "Create Template"}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Paper elevation={2} sx={{ p: 2, mt: 3, backgroundColor: '#e3f2fd', border: '1px solid #2196f3' }}>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                            We'll extract nouns, adjectives, and verbs from your content and filter out banned words.
                            A template will be created in Draft version. You can add clues and configure the crossword in the 'Cross Board' menu.
                        </Typography>
                    </Paper>
                </Grid>               
            </Grid>

            <CustomSnackbar
                open={openSnackbar}
                setOpen={setOpenSnackbar}
                severity={severity}
                message={message}
            />
        </Box>
    );
}

